import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RecordedVideo, RECORDING_CONFIG, getAdaptiveBitrate } from '../types';
import { logger } from '@/services/loggerService';
import { errorReporter } from '@/services/errorReportingService';

const isTypeSupportedSafe = (type: string): boolean => {
  try {
    return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
  } catch {
    return false;
  }
};

export const pickMime = (): { mime: string; ext: string } => {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
    return { mime: 'video/webm;codecs="vp8,opus"', ext: 'webm' };
  }

  const userAgent = navigator.userAgent ?? '';
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  const webmCandidates: Array<{ mime: string; ext: string }> = [
    { mime: 'video/webm;codecs="vp8,opus"', ext: 'webm' },
    { mime: 'video/webm;codecs="vp9,opus"', ext: 'webm' },
    { mime: 'video/webm;codecs="vp8"', ext: 'webm' },
    { mime: 'video/webm;codecs="vp9"', ext: 'webm' },
    { mime: 'video/webm', ext: 'webm' },
  ];

  for (const candidate of webmCandidates) {
    if (isTypeSupportedSafe(candidate.mime)) {
      return candidate;
    }
  }

  if (isSafari) {
    const safariFallback = { mime: 'video/mp4;codecs="avc1.42E01E, mp4a.40.2"', ext: 'mp4' };
    if (isTypeSupportedSafe(safariFallback.mime)) {
      return safariFallback;
    }
  }

  return { mime: 'video/webm', ext: 'webm' };
};

interface UseMediaRecorderOptions {
  maxDuration?: number;
  resolution?: number;
  isMobile?: boolean;
  onRecordingComplete?: (video: RecordedVideo) => void;
}

interface UseMediaRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  elapsed: number;
  recordedVideo: RecordedVideo | null;
  error: string | null;
  startRecording: (stream: MediaStream, cleanupStream?: () => void) => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => void;
  resetRecording: () => void;
  clearError: () => void;
  clearRecordedVideo: () => void;
}

export function useMediaRecorder(options: UseMediaRecorderOptions = {}): UseMediaRecorderReturn {
  const { maxDuration = RECORDING_CONFIG.DEFAULT_MAX_DURATION, resolution, isMobile, onRecordingComplete } = options;

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const pauseStartTimeRef = useRef<number | null>(null);
  const pausedDurationRef = useRef(0);
  const elapsedRef = useRef(0);
  const cleanupStreamRef = useRef<(() => void) | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recordedVideo, setRecordedVideo] = useState<RecordedVideo | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Codec support can't change during a session, so resolve it once. (Recomputing
  // it every render used to be what accidentally kept the Recorder restart effect
  // alive — that now has an explicit restartTick dependency instead.)
  const mimeInfo = useMemo(() => pickMime(), []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Read the cap through a ref. Plan features resolve asynchronously, so a
  // recording started before pricing loads would otherwise be capped — and have
  // its reported duration clamped — at the 120s default for its whole lifetime,
  // even on a 30-minute plan.
  const maxDurationRef = useRef(maxDuration);
  useEffect(() => {
    maxDurationRef.current = maxDuration;
  }, [maxDuration]);

  // Mirror the recorded video so unmount cleanup can revoke its object URL
  // without adding it as an effect dependency.
  const recordedVideoRef = useRef<RecordedVideo | null>(null);

  // Keep elapsedRef in sync
  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  useEffect(() => {
    recordedVideoRef.current = recordedVideo;
  }, [recordedVideo]);

  const getVideoDuration = useCallback((file: File, fallbackSeconds: number): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const url = URL.createObjectURL(file);

      let settled = false;

      const cleanup = () => {
        // Clear the safety timeout too — otherwise it stays pending after the
        // promise settles and fires against a torn-down video element.
        window.clearTimeout(timeoutId);
        URL.revokeObjectURL(url);
        video.removeAttribute('src');
        video.load();
      };

      const resolveWith = (value: number) => {
        if (!settled) {
          settled = true;
          cleanup();
          resolve(value);
        }
      };

      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : fallbackSeconds;
        resolveWith(duration);
      };
      video.onerror = () => {
        resolveWith(fallbackSeconds);
      };

      // Arm the safety timeout BEFORE kicking off the load, so `cleanup` always
      // has a defined id to clear no matter which path settles first.
      const timeoutId = window.setTimeout(() => {
        resolveWith(fallbackSeconds);
      }, 2000);

      video.src = url;
    });
  }, []);

  const handleStop = useCallback(async () => {
    if (!recorderRef.current) return;

    clearTimer();
    setIsRecording(false);
    setIsPaused(false);
    const startedAt = recordingStartTimeRef.current;
    const pauseStartedAt = pauseStartTimeRef.current;
    const pausedDurationMs = pausedDurationRef.current;
    recordingStartTimeRef.current = null;
    pauseStartTimeRef.current = null;
    pausedDurationRef.current = 0;
    recorderRef.current = null;

    const { mime, ext } = mimeInfo;
    const baseType = mime.split(';')[0];

    try {
      if (chunksRef.current.length === 0) {
        setError('No video data was recorded. Please try again.');
        cleanupStreamRef.current?.();
        return;
      }

      const blob = new Blob(chunksRef.current, { type: baseType });
      const blobSize = blob.size;

      chunksRef.current = [];

      if (blobSize === 0) {
        setError('Recorded video was empty. Please try again.');
        cleanupStreamRef.current?.();
        return;
      }

      const fileName = `recording-${Date.now()}.${ext}`;
      const file = new File([blob], fileName, { type: baseType });
      const initialFallback = Math.max(elapsedRef.current, 1);
      const metadataDuration = await getVideoDuration(file, initialFallback).catch(() => {
        return Number.NaN;
      });

      const measuredDurationFromStart = (() => {
        if (typeof startedAt === 'number') {
          const pendingPauseMs = pauseStartedAt ? Date.now() - pauseStartedAt : 0;
          const pausedMs = pausedDurationMs + Math.max(pendingPauseMs, 0);
          const elapsedMs = Math.max(Date.now() - startedAt - pausedMs, 0);
          return elapsedMs / 1000;
        }
        return 0;
      })();

      const fallbackDuration = Math.max(measuredDurationFromStart, elapsedRef.current, 0.1);
      const resolvedDuration =
        Number.isFinite(metadataDuration) && metadataDuration > 0 ? metadataDuration : fallbackDuration;

      if (!Number.isFinite(resolvedDuration) || resolvedDuration <= 0) {
        setError('Recorded video duration could not be determined. Please try again.');
        return;
      }

      const safeDuration = Math.min(resolvedDuration, maxDurationRef.current);
      const url = URL.createObjectURL(file);

      const videoPayload: RecordedVideo = {
        file,
        duration: safeDuration,
        url,
        name: fileName,
      };

      setRecordedVideo((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev.url);
        }
        return videoPayload;
      });
      recordedVideoRef.current = videoPayload;

      onRecordingComplete?.(videoPayload);
    } catch (err) {
      logger.warn('video', 'Failed to process recording', err);
      setError('Failed to process the recording. Please try again.');
    } finally {
      cleanupStreamRef.current?.();
    }
  }, [clearTimer, mimeInfo, getVideoDuration, onRecordingComplete]);

  const handleDataAvailable = useCallback((event: BlobEvent) => {
    if (event.data && event.data.size > 0) {
      chunksRef.current.push(event.data);
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = window.setInterval(() => {
      const cap = maxDurationRef.current;
      const nextValue = elapsedRef.current + 1;
      const clamped = Math.min(nextValue, cap);
      elapsedRef.current = clamped;
      setElapsed(clamped);

      // Stop OUTSIDE the state updater. Calling recorder.stop() from inside a
      // setState updater runs it during the render phase, where React may
      // double-invoke it (StrictMode) — the sibling audio recorder does it here.
      if (nextValue >= cap && recorderRef.current && recorderRef.current.state === 'recording') {
        recorderRef.current.stop();
      }
    }, 1000);
  }, [clearTimer]);

  const startRecording = useCallback(
    (stream: MediaStream, cleanupStream?: () => void) => {
      if (isRecording) return;

      setError(null);
      setIsPaused(false);
      setRecordedVideo((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev.url);
        }
        return null;
      });
      chunksRef.current = [];
      cleanupStreamRef.current = cleanupStream ?? null;

      const startRecorderInstance = (recorder: MediaRecorder) => {
        recorderRef.current = recorder;
        recorder.ondataavailable = handleDataAvailable;
        recorder.onstop = handleStop;

        recordingStartTimeRef.current = Date.now();
        pauseStartTimeRef.current = null;
        pausedDurationRef.current = 0;
        elapsedRef.current = 0;

        recorder.start(RECORDING_CONFIG.CHUNK_INTERVAL);
        setIsRecording(true);
        setElapsed(0);
        startTimer();
      };

      try {
        const { mime } = mimeInfo;
        const { videoBitrate, audioBitrate } = resolution
          ? getAdaptiveBitrate(resolution, isMobile ?? false)
          : { videoBitrate: RECORDING_CONFIG.BASE_VIDEO_BITRATE, audioBitrate: RECORDING_CONFIG.BASE_AUDIO_BITRATE };
        const recorderOptions: MediaRecorderOptions = {
          mimeType: mime,
          videoBitsPerSecond: videoBitrate,
          audioBitsPerSecond: audioBitrate,
        };

        const recorder = new MediaRecorder(stream, recorderOptions);
        startRecorderInstance(recorder);
      } catch (primaryErr) {
        // Primary codec options failed, try without specific codec settings
        logger.warn('video', 'Primary MediaRecorder options failed, trying fallback', primaryErr);
        try {
          const recorder = new MediaRecorder(stream);
          startRecorderInstance(recorder);
        } catch (fallbackErr) {
          logger.warn('video', 'Fallback MediaRecorder also failed', fallbackErr);
          setError('Recording is not supported in this browser.');
          cleanupStreamRef.current?.();
        }
      }
    },
    [isRecording, mimeInfo, resolution, isMobile, handleDataAvailable, handleStop, startTimer],
  );

  const pauseRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') {
      return;
    }
    try {
      recorder.pause();
      pauseStartTimeRef.current = Date.now();
      setIsPaused(true);
      clearTimer();
    } catch (err) {
      errorReporter.expected('Failed to pause MediaRecorder', err, {
        feature: 'recording',
        operation: 'pauseRecording',
      });
      setError('Failed to pause recording. Please try again.');
    }
  }, [clearTimer]);

  const resumeRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'paused') {
      return;
    }
    try {
      recorder.resume();
      if (pauseStartTimeRef.current) {
        pausedDurationRef.current += Math.max(Date.now() - pauseStartTimeRef.current, 0);
      }
      pauseStartTimeRef.current = null;
      setIsPaused(false);
      startTimer();
    } catch (err) {
      errorReporter.expected('Failed to resume MediaRecorder', err, {
        feature: 'recording',
        operation: 'resumeRecording',
      });
      setError('Failed to resume recording. Please try again.');
    }
  }, [startTimer]);

  const stopRecording = useCallback(() => {
    if (!recorderRef.current) return;
    if (recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch (err) {
        logger.warn('video', 'Failed to stop MediaRecorder', err);
        setError('Failed to stop recording. Please try again.');
        setIsRecording(false);
        setIsPaused(false);
        clearTimer();
        chunksRef.current = [];
        recorderRef.current = null;
        cleanupStreamRef.current?.();
      }
    }
  }, [clearTimer]);

  const resetRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    cleanupStreamRef.current?.();
    setIsRecording(false);
    setIsPaused(false);
    setElapsed(0);
    elapsedRef.current = 0;
    chunksRef.current = [];
    recorderRef.current = null;
    clearTimer();
    recordingStartTimeRef.current = null;
    pauseStartTimeRef.current = null;
    pausedDurationRef.current = 0;
    setRecordedVideo((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });
    setError(null);
  }, [clearTimer]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearRecordedVideo = useCallback(() => {
    setRecordedVideo((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        // Detach handlers BEFORE stopping. Leaving `onstop` attached lets
        // handleStop run after unmount: it assembles the blob and calls
        // URL.createObjectURL on it, and nothing is left mounted to revoke it —
        // navigating away mid-recording retained hundreds of MB for the lifetime
        // of the document. The audio recorder already does this.
        recorderRef.current.onstop = null;
        recorderRef.current.ondataavailable = null;
        recorderRef.current.stop();
      }
      chunksRef.current = [];
      // Release the finished recording's object URL too — clearRecordedVideo
      // only runs on explicit user action, never on unmount.
      if (recordedVideoRef.current) {
        URL.revokeObjectURL(recordedVideoRef.current.url);
        recordedVideoRef.current = null;
      }
      clearTimer();
      cleanupStreamRef.current?.();
    };
  }, [clearTimer]);

  return {
    isRecording,
    isPaused,
    elapsed,
    recordedVideo,
    error,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
    clearError,
    clearRecordedVideo,
  };
}
